"use client";

import { AlertTriangle, HelpCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { SummaryGridSkeleton } from "@/components/shared/loading-skeleton";
import { ErrorState } from "@/components/shared/error-state";
import type { AnalyticsSummaryApiResponse } from "@/types/api";

interface SummaryCardDef {
  key: keyof AnalyticsSummaryApiResponse | "errorRatePct";
  label: string;
  tooltip: string;
  format: (summary: AnalyticsSummaryApiResponse) => string;
}

const CARDS: SummaryCardDef[] = [
  {
    key: "totalRequests",
    label: "Total requests",
    tooltip: "DynamoDB count for the current Created window (last 2 days by default).",
    format: (s) => s.totalRequests.toLocaleString(),
  },
  {
    key: "totalErrors",
    label: "Total errors",
    tooltip: "Records classified as error or malformed.",
    format: (s) => (s.resultMayBePartial ? "…" : s.totalErrors.toLocaleString()),
  },
  {
    key: "errorRatePct",
    label: "Error rate",
    tooltip: "totalErrors / totalRequests × 100. Shows 0% when there are no requests.",
    format: (s) => (s.resultMayBePartial ? "…" : `${s.errorRate.toFixed(1)}%`),
  },
  {
    key: "successCount",
    label: "Successful",
    tooltip: "Responses with clear evidence of success (2xx status, success: true, or ok: true).",
    format: (s) => (s.resultMayBePartial ? "…" : s.successCount.toLocaleString()),
  },
  {
    key: "unknownRecords",
    label: "Unknown",
    tooltip: "Responses with no clear success or error evidence.",
    format: (s) => (s.resultMayBePartial ? "…" : s.unknownRecords.toLocaleString()),
  },
  {
    key: "malformedRecords",
    label: "Malformed",
    tooltip: "Responses whose JSON payload could not be parsed. Always counted as errors.",
    format: (s) => (s.resultMayBePartial ? "…" : s.malformedRecords.toLocaleString()),
  },
  {
    key: "uniqueUsers",
    label: "Unique users",
    tooltip: "Distinct userId values in the current window.",
    format: (s) => (s.resultMayBePartial ? "…" : s.uniqueUsers.toLocaleString()),
  },
  {
    key: "uniqueRoutes",
    label: "Unique routes",
    tooltip: "Distinct normalized routes in the current window.",
    format: (s) => (s.resultMayBePartial ? "…" : s.uniqueRoutes.toLocaleString()),
  },
  {
    key: "storedResponses",
    label: "Stored responses",
    tooltip: "Records with responseStored = true.",
    format: (s) => (s.resultMayBePartial ? "…" : s.storedResponses.toLocaleString()),
  },
];

interface DashboardSummaryProps {
  summary: AnalyticsSummaryApiResponse | null;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
}

export function DashboardSummary({ summary, isLoading, error, onRetry }: DashboardSummaryProps) {
  if (isLoading && !summary) {
    return <SummaryGridSkeleton />;
  }

  if (error) {
    return <ErrorState title="Could not load summary metrics" onRetry={onRetry} />;
  }

  if (!summary) return null;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-9">
        {CARDS.map((card) => (
          <Card key={card.key}>
            <CardHeader className="flex flex-row items-center justify-between gap-1 pb-1 space-y-0">
              <CardTitle className="text-xs font-medium text-muted-foreground">{card.label}</CardTitle>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label={`About ${card.label}`} className="text-muted-foreground/70">
                    <HelpCircle className="size-3.5" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-56">{card.tooltip}</TooltipContent>
              </Tooltip>
            </CardHeader>
            <CardContent>
              <p className="text-xl font-semibold tabular-nums">{card.format(summary)}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span>
          {summary.resultMayBePartial
            ? `Total requests ready (${summary.totalRequests.toLocaleString()}) · refining other metrics…`
            : "Exact counts for the selected Created window (last 2 days by default)"}{" "}
          · updated {formatDistanceToNow(new Date(summary.computedAt), { addSuffix: true })}
        </span>
        {summary.resultMayBePartial ? (
          <span className="flex items-center gap-1 text-warning">
            <AlertTriangle className="size-3.5" aria-hidden="true" />
            Charts appear after exact metrics finish.
          </span>
        ) : null}
      </div>
    </div>
  );
}
